'use client';

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  url: string;
}

// Royalty-free MP3 tracks from Pixabay - Ocean/Relaxing themed
export const AVAILABLE_TRACKS: MusicTrack[] = [
  {
    id: 'ocean-waves',
    title: 'Ocean Waves',
    artist: 'Relaxing Sounds',
    url: 'https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3' // Ocean Waves - Pixabay
  }
];

interface MusicContextType {
  isPlaying: boolean;
  isMusicEnabled: boolean;
  volume: number;
  currentTrack: MusicTrack;
  loadError: string | null;
  aiRecommendation: { reasoning: string; mood: string } | null;
  setVolume: (volume: number) => void;
  setCurrentTrack: (track: MusicTrack) => void;
  toggleMusic: () => Promise<void>;
  requestMusicRecommendation: (destination?: string, timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night') => Promise<void>;
}

const MusicContext = createContext<MusicContextType | undefined>(undefined);

export const MusicProvider = ({ children }: { children: ReactNode }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMusicEnabled, setIsMusicEnabled] = useState(true); // Changed to true for auto-play
  const [volume, setVolumeState] = useState(0.3);
  const [currentTrack, setCurrentTrackState] = useState<MusicTrack>(AVAILABLE_TRACKS[0]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasAttemptedAutoPlay, setHasAttemptedAutoPlay] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { user } = useAuth();
  const supabase = createClient();

  // Initialize Audio Element
  useEffect(() => {
    if (typeof window !== 'undefined' && !audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = true;
      audioRef.current.volume = volume;
      audioRef.current.src = currentTrack.url;
      audioRef.current.preload = 'auto';

      audioRef.current.addEventListener('error', () => {
        setLoadError('Failed to load audio track');
        setIsPlaying(false);
      });

      audioRef.current.addEventListener('canplay', () => {
        setLoadError(null);
      });

      audioRef.current.addEventListener('play', () => {
        setIsPlaying(true);
      });

      audioRef.current.addEventListener('pause', () => {
        setIsPlaying(false);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Handle track changes
  const setCurrentTrack = (track: MusicTrack) => {
    setCurrentTrackState(track);
    setLoadError(null);
    
    if (audioRef.current) {
      const wasPlaying = !audioRef.current.paused;
      audioRef.current.src = track.url;
      
      if (wasPlaying && isMusicEnabled) {
        audioRef.current.play().catch((error) => {
          console.error('Play error:', error);
          setLoadError('Unable to play audio. Please interact with the page first.');
          setIsPlaying(false);
        });
      }
    }
    
    if (typeof window !== 'undefined') {
      localStorage.setItem('currentTrackId', track.id);
    }
  };

  // Toggle music on/off
  const toggleMusic = async () => {
    const newState = !isMusicEnabled;
    setIsMusicEnabled(newState);
    
    if (audioRef.current) {
      if (newState) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          setLoadError(null);
        } catch (error) {
          console.error('Play error:', error);
          setLoadError('Unable to play audio. Browser autoplay policy may be blocking playback.');
          setIsPlaying(false);
          setIsMusicEnabled(false);
        }
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
    
    if (typeof window !== 'undefined') {
      localStorage.setItem('musicEnabled', newState.toString());
    }
  };

  // Handle volume changes
  const setVolume = (newVolume: number) => {
    setVolumeState(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem('musicVolume', newVolume.toString());
    }
  };

  // Load preferences from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedVolume = localStorage.getItem('musicVolume');
      if (savedVolume) {
        const vol = parseFloat(savedVolume);
        setVolumeState(vol);
        if (audioRef.current) {
          audioRef.current.volume = vol;
        }
      }

      const savedTrackId = localStorage.getItem('currentTrackId');
      if (savedTrackId) {
        const track = AVAILABLE_TRACKS.find(t => t.id === savedTrackId);
        if (track) {
          setCurrentTrackState(track);
          if (audioRef.current) {
            audioRef.current.src = track.url;
          }
        }
      }

      const savedMusicEnabled = localStorage.getItem('musicEnabled');
      if (savedMusicEnabled === 'false') {
        setIsMusicEnabled(false);
      } else {
        // Auto-play music on page load
        setIsMusicEnabled(true);
      }
    }
  }, []);

  // Auto-play music when component mounts and music is enabled
  useEffect(() => {
    if (isMusicEnabled && !hasAttemptedAutoPlay && audioRef.current) {
      setHasAttemptedAutoPlay(true);
      
      // Attempt to play after a short delay to ensure audio is loaded
      const playTimer = setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch((error) => {
            console.log('Auto-play blocked by browser. User interaction required.');
            setLoadError('Click the music icon to start playback');
            setIsPlaying(false);
          });
        }
      }, 500);

      return () => clearTimeout(playTimer);
    }
  }, [isMusicEnabled, hasAttemptedAutoPlay]);

  // Load volume from Supabase if user is logged in
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('user_music_preferences')
          .select('volume, music_enabled')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          return;
        }

        if (data) {
          const vol = parseFloat(data.volume);
          setVolumeState(vol);
          if (audioRef.current) {
            audioRef.current.volume = vol;
          }
          
          setIsMusicEnabled(data.music_enabled);
          if (!data.music_enabled && audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
          }
        } else {
          await supabase.from('user_music_preferences').insert({
            user_id: user.id,
            music_enabled: true,
            volume: 0.3,
          });
        }
      } catch (error) {
        console.error('Error loading music preferences:', error);
      }
    };

    loadUserPreferences();
  }, [user]);

  // Save volume and music state to Supabase when they change
  useEffect(() => {
    const saveUserPreferences = async () => {
      if (!user) return;

      try {
        await supabase
          .from('user_music_preferences')
          .upsert({
            user_id: user.id,
            music_enabled: isMusicEnabled,
            volume: volume.toString(),
            updated_at: new Date().toISOString(),
          });
      } catch (error) {
        console.error('Error saving music preferences:', error);
      }
    };

    const debounceTimer = setTimeout(saveUserPreferences, 500);
    return () => clearTimeout(debounceTimer);
  }, [volume, isMusicEnabled, user]);

  return (
    <MusicContext.Provider data-component-id="src/contexts/MusicContext.tsx:267:4" data-component-path="src/contexts/MusicContext.tsx" data-component-line="267" data-component-end-line="292" data-component-file="MusicContext.tsx" data-component-name="MusicContext.Provider" data-component-content="%7B%22elementName%22%3A%22MusicContext.Provider%22%7D"
      value={{
        isPlaying,
        isMusicEnabled,
        volume,
        currentTrack,
        loadError,
        setVolume,
        setCurrentTrack,
        toggleMusic,
        requestMusicRecommendation: async () => {
          // Placeholder for AI recommendation logic
          console.log('Requesting music recommendation...');
          await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network request
          const recommendation = {
            reasoning: 'Based on your listening history and preferences, we recommend this track.',
            mood: 'Relaxed',
          };
          console.log('Recommendation:', recommendation);
          // For now, just update the current track
          setCurrentTrack(AVAILABLE_TRACKS[0]);
        },
      }}
    >
      {children}
    </MusicContext.Provider>
  );
};

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (context === undefined) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
};